"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { ArrowUpRight, Search, X } from "lucide-react";
import { venueSearchText, type VenueListing } from "@/lib/venue-directory";

export default function VenueDirectory({ venues }: { venues: VenueListing[] }) {
  const [query, setQuery] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const countries = useMemo(() => [...new Set(venues.map((venue) => venue.country).filter(Boolean))].sort(), [venues]);
  const cities = useMemo(() => {
    const counts = new Map<string, number>();
    for (const venue of venues) if (venue.city && (!country || venue.country === country)) counts.set(venue.city, (counts.get(venue.city) || 0) + 1);
    return [...counts].sort((a, b) => a[0].localeCompare(b[0]));
  }, [venues, country]);
  const visible = useMemo(() => {
    const terms = venueSearchText(query).split(/\s+/).filter(Boolean);
    return venues.filter((venue) => {
      if ((country && venue.country !== country) || (city && venue.city !== city)) return false;
      const text = venueSearchText([venue.name, venue.city, venue.country, ...venue.musicGenres].join(" "));
      return terms.every((term) => text.includes(term));
    });
  }, [venues, query, country, city]);
  const filtered = !!(query || country || city);
  const reset = () => { setQuery(""); setCountry(""); setCity(""); };
  const field = "h-12 w-full border border-line-strong bg-canvas px-4 text-sm text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent";

  return (
    <section aria-labelledby="venue-directory-title" className="mt-10 sm:mt-14">
      <h2 id="venue-directory-title" className="sr-only">Browse venues</h2>
      <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_220px]">
        <div>
          <label htmlFor="venue-query" className="mb-2 block text-xs font-bold uppercase tracking-wider text-muted">Find a venue</label>
          <div className="relative">
            <Search size={18} aria-hidden="true" className="pointer-events-none absolute left-4 top-4 text-muted" />
            <input id="venue-query" type="search" maxLength={80} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name, city or music style" className={`${field} pl-11`} autoComplete="off" />
          </div>
        </div>
        <div>
          <label htmlFor="venue-country" className="mb-2 block text-xs font-bold uppercase tracking-wider text-muted">Country</label>
          <select id="venue-country" value={country} onChange={(event) => { setCountry(event.target.value); setCity(""); }} className={field}>
            <option value="">All countries</option>
            {countries.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-2" role="group" aria-label="Filter venues by city">
        <button type="button" aria-pressed={!city} onClick={() => setCity("")} className={`min-h-11 border px-4 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${!city ? "border-ink bg-ink text-canvas" : "border-line text-muted hover:border-line-strong hover:text-ink"}`}>All cities</button>
        {cities.map(([name, count]) => <button key={name} type="button" aria-pressed={city === name} onClick={() => setCity(city === name ? "" : name)} className={`min-h-11 border px-4 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${city === name ? "border-ink bg-ink text-canvas" : "border-line text-muted hover:border-line-strong hover:text-ink"}`}>{name} <span className="ml-1.5 text-xs tabular-nums">{count}</span></button>)}
      </div>
      <div className="mt-9 flex min-h-11 flex-wrap items-center justify-between gap-3 border-b border-line-strong pb-4">
        <p role="status" aria-live="polite" aria-atomic="true" className="text-sm text-muted">{visible.length} {visible.length === 1 ? "venue" : "venues"}{city ? ` in ${city}` : country ? ` in ${country}` : " in the directory"}</p>
        {filtered && <button type="button" onClick={reset} className="inline-flex min-h-11 items-center gap-2 text-sm text-ink hover:text-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"><X size={14} aria-hidden="true" /> Clear filters</button>}
      </div>
      {visible.length ? (
        <ul className="grid gap-x-12 lg:grid-cols-2" data-venue-directory>
          {visible.map((venue) => (
            <li key={venue.slug} className="min-w-0 border-b border-line">
              <Link href={`/venue/${venue.slug}`} className="group flex h-full min-h-36 items-start justify-between gap-5 py-7 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent">
                <div className="min-w-0">
                  <p className="mb-2 text-xs font-semibold text-muted">{[venue.city, venue.country].filter(Boolean).join(", ")}</p>
                  <h3 className="break-words text-2xl font-extrabold leading-tight tracking-tight text-ink group-hover:text-accent">{venue.name}</h3>
                  {venue.musicGenres.length > 0 && <p className="mt-3 text-xs capitalize leading-relaxed text-muted">{venue.musicGenres.join(" · ")}</p>}
                </div>
                <ArrowUpRight size={20} aria-hidden="true" className="mt-1 shrink-0 text-muted group-hover:text-accent" />
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="border-b border-line py-16 text-center">
          <h3 className="text-xl font-bold text-ink">{venues.length ? "No venues match your search" : "More venue profiles are on the way"}</h3>
          <p className="mt-3 text-sm text-muted">{venues.length ? "Try another name or city, or clear the filters." : "Browse upcoming events while we add to the directory."}</p>
          {venues.length ? <button type="button" onClick={reset} className="mt-5 min-h-11 px-4 font-semibold text-accent underline underline-offset-4">Show all venues</button> : <Link href="/events" className="mt-5 inline-flex min-h-11 items-center font-semibold text-accent underline underline-offset-4">Explore events</Link>}
        </div>
      )}
    </section>
  );
}
